import { useCallback } from "react";
import { useNavigate, useResolvedPath } from "react-router";

/**
 * The one back control for the app: real browser back when this tab has an
 * in-app page to return to, so the previous URL comes back exactly as it was.
 *
 * React Router numbers the history entries it creates (history.state.idx,
 * starting at 0 on the entry the visitor arrived on), so idx > 0 proves the
 * previous entry is ours. idx === 0 is a deep link, where back would step off
 * the site, so we go to `fallback`, which defaults to the route parent.
 *
 * Pass `fallback` only when the route tree cannot answer: a flat sibling route
 * (/news/:slug lives beside /news) or a page reachable from anywhere.
 */
export const useGoBack = (options?: { fallback?: string }): (() => void) => {
  const navigate = useNavigate();
  const routeParent = useResolvedPath("..").pathname;
  const fallback = options?.fallback ?? routeParent;

  return useCallback(() => {
    // Read at click time: idx changes with every navigation.
    const idx: unknown = window.history.state?.idx;
    if (typeof idx === "number" && idx > 0) {
      // eslint-disable-next-line no-restricted-syntax -- the one guarded call the rule points everyone to
      navigate(-1);
    } else {
      navigate(fallback);
    }
  }, [navigate, fallback]);
};
